import styled from 'styled-components'
import { Button } from 'react-bootstrap'

export const ReportContainer = styled.div`
  padding: 20px 0;
  background: #f8f9fa;
  min-height: 100vh;
`

export const ReportHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  color: white;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
`

export const ReportTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

export const ReportSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 15px;
  opacity: 0.9;
`

export const LastUpdated = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  font-style: italic;
`

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 20px;
  }
`

export const RefreshButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  }

  &:disabled {
    opacity: 0.6;
  }
`

export const PDFButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #28a745;
  border: none;

  &:hover {
    background: #218838;
  }
`

export const ReportFooter = styled.div`
  margin-top: 40px;
  padding: 30px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  text-align: center;
  color: #6c757d;

  p {
    margin: 0;
    line-height: 1.6;
  }
`

export const MetricCardContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`

export const MetricValue = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`

export const MetricTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const MetricSubtitle = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
`

export const MetricIcon = styled.div<{ color?: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white !important;
  flex-shrink: 0;
  background: ${(props) => {
    switch (props.color) {
      case 'success':
        return 'linear-gradient(135deg, #28a745, #20c997)'
      case 'warning':
        return 'linear-gradient(135deg, #ffc107, #fd7e14)'
      case 'danger':
        return 'linear-gradient(135deg, #dc3545, #e83e8c)'
      case 'info':
        return 'linear-gradient(135deg, #17a2b8, #6f42c1)'
      default:
        return 'linear-gradient(135deg, #007bff, #6610f2)'
    }
  }};

  i {
    color: white !important;
    font-size: inherit;
    line-height: 1;
  }
`

export const ChangeIndicator = styled.div<{ trend: 'up' | 'down' | 'stable' }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  margin-top: 10px;

  color: ${(props) => {
    switch (props.trend) {
      case 'up':
        return '#28a745'
      case 'down':
        return '#dc3545'
      default:
        return '#6c757d'
    }
  }};
`

export const TransactionHash = styled.code`
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #495057;
  font-family: 'Monaco', 'Consolas', monospace;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e9ecef;
    color: #007bff;
  }
`

export const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case 'healthy':
        return `
          background: #d4edda;
          color: #155724;
        `
      case 'pending':
        return `
          background: #fff3cd;
          color: #856404;
        `
      case 'paid':
        return `
          background: #d4edda;
          color: #155724;
        `
      case 'overdue':
        return `
          background: #f8d7da;
          color: #721c24;
        `
      default:
        return `
          background: #d1ecf1;
          color: #0c5460;
        `
    }
  }}
`

export const TableContainer = styled.div`
  .table {
    margin-bottom: 0;

    th {
      background: #f8f9fa;
      border-top: none;
      font-weight: 600;
      color: #495057;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
      padding: 15px 12px;
    }

    td {
      padding: 12px;
      vertical-align: middle;
      border-color: #e9ecef;
    }

    tbody tr:hover {
      background: #f8f9fa;
    }
  }
`

export const ChartContainer = styled.div`
  .apexcharts-canvas {
    margin: 0 auto;
  }

  .apexcharts-legend {
    justify-content: center !important;
  }
`

export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  .spinner-border {
    width: 3rem;
    height: 3rem;
    color: #007bff;
  }
`

export const OverviewCardsContainer = styled.div`
  margin-bottom: 30px;

  .row > .col-lg-3 {
    margin-bottom: 20px;
  }
`
